"use client"
import { Card, CardContent } from "@/components/shared/ui/card"
import "@/styles/auth.css"
import { Input } from "@/components/shared/ui/input"
import { EyeIcon, EyeOffIcon } from "lucide-react"
import { Button } from "@/components/shared/ui/button"
import { useTranslations } from "next-intl"
import { Link } from "@/navigation"
import Image from "next/image"
import { useEffect, useState } from "react"
import useAuthStore from "@/store/authStore"
import { useRouter } from "@/navigation"
import { SignupForm } from "@/types/authTypes"
import { setLocalStorage } from "@/lib/localStorage"

const SignupPage = () => {
    const [showPassword, setShowPassword] = useState(false);

    const [form, setForm] = useState<SignupForm>({
        fullName: "",
        email: "",
        password: "",
        role: "user"
    })

    const { isLoading, signup, handleAuthCallback, loginWithGoogle, loginWithFacebook } = useAuthStore()

    const router = useRouter()

    const handleSignup = async (e: any) => {
        e.preventDefault();

        const res = await signup(form)

        if (res?.status && res.status === 200) {
            // Persist the email so the verify page can show the real address
            setLocalStorage("verifyEmail", form.email)
            router.push("/auth/verify")
        }
    }

    const t = useTranslations("Auth.signup")

    useEffect(() => {
        const redirect = async () => {
            const res = await handleAuthCallback()
            if (res && res) {
                router.push("/")
            }
        }

        redirect()
    }, []);

    if (isLoading) {
        return <div className='min-h-screen flex items-center justify-center'>
            <p>Loading...</p>
        </div>
    }

    return (
        <div className='w-full signup-bg'>
            <div className='form-container '>
                <h1 className="font-bold text-xl">
                    {t("title")}
                </h1>

                <Card className="w-full max-w-md mx-auto">
                    <CardContent className="py-4">
                        <div className="space-y-4">
                            <div className="space-y-2">
                                <label className="text-right block text-sm font-medium">
                                    {t("name")}
                                </label>
                                <Input
                                    type="text"
                                    placeholder={t("name")}
                                    className="text-right"
                                    onChange={(e) => setForm({ ...form, fullName: e.target.value })}
                                />
                            </div>

                            <div className="space-y-2">
                                <label className="text-right block text-sm font-medium">
                                    {t("email")}
                                </label>
                                <Input
                                    type="email"
                                    placeholder={t("email")}
                                    className="text-right"
                                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                                />
                            </div>

                            <div className="space-y-2">
                                <label className="text-right block text-sm font-medium">
                                    {t("password")}
                                </label>
                                <div className="relative">
                                    <Input
                                        type={showPassword ? "text" : "password"}
                                        placeholder={t("password")}
                                        className="text-right pr-4"
                                        onChange={(e) => setForm({ ...form, password: e.target.value })}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500 hover:text-gray-700"
                                    >
                                        {showPassword ? <EyeOffIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
                                    </button>
                                </div>
                            </div>

                            <Button type="submit" disabled={isLoading} onClick={handleSignup} variant="sub_primary" className="w-full rounded-xl p-5">
                                {isLoading ? "...Loading" : t("signup")}
                            </Button>
                        </div>
                    </CardContent>
                </Card>

                <Card className="w-full max-w-md mx-auto">
                    <CardContent >
                        <div className="space-y-4">
                            <div className="text-center w-full py-2">
                                <span className="font-bold bg-white text-gray-500">
                                    {t("or")}
                                </span>
                            </div>

                            <Button onClick={async () => {
                                await loginWithGoogle();
                            }}
                                variant="outline" className="w-full">
                                <Image
                                    width={20}
                                    height={20}
                                    src="/goggle-logo.webp"
                                    alt="Google"
                                    className="h-5 w-5 mr-2"
                                />
                                {t("google")}
                            </Button>

                            {/* <Button onClick={async () => {
                                await loginWithFacebook();
                            }}
                                variant="outline" className="w-full">
                                <Image
                                    width={20}
                                    height={20}
                                    src="/face-logo.webp"
                                    alt="Facebook"
                                    className="h-5 w-5 mr-2"
                                />
                                {t("facebook")}
                            </Button> */}

                            <div className="text-center text-sm font-bold">
                                <span className="text-gray-600">
                                    {t("login1")}
                                </span>
                                {" "}
                                <Link href="/auth/login">
                                    <span className="text-secondary decoration-secondary underline">
                                        {t("login2")}
                                    </span>
                                </Link>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    )
}

export default SignupPage